import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Linkedin, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getVisitorFingerprint } from "../lib/fingerprint";
import ExploreLinks from "./ExploreLinks";

export default function Footer() {
  const { pathname } = useLocation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const visitor_fingerprint = await getVisitorFingerprint();
      await api.post("/leads", { email, source: "newsletter", name: "Newsletter Subscriber", visitor_fingerprint });
      toast.success("You're on the list. Expect the first memo soon.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
    setSubmitting(false);
  };

  if (pathname.startsWith("/crm")) return null;

  return (
    <footer className="bg-ink text-bone" data-testid="site-footer">
      <div className="px-6 md:px-12 lg:px-20 pt-24 pb-10">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Brand column */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/sofia-logo.webp"
                width={480}
                height={320}
                alt="Sofia Sands Realty"
                className="w-20 h-20 object-contain"
                data-testid="footer-logo"
              />
              <div className="font-display text-[2rem] leading-none tracking-tighter">Sofia Sands</div>
            </div>
            <p className="text-bone/70 text-base leading-relaxed max-w-md font-light">
              A boutique for discerning buyers. Fewer than fifty private clients a year,
              each one accompanied by an analyst's memo, a licensed broker, and Sofia — our
              AI concierge who answers in minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="stamp stamp-rera" data-testid="footer-rera-stamp">RERA 41793</span>
              <span className="stamp bg-bone/10 text-bone/80">Dubai &middot; RAK</span>
            </div>
          </div>

          {/* Nav */}
          {/* Nav */}
          <div className="md:col-span-3">
            <div className="overline overline-gold mb-5">Explore</div>
            <ul className="space-y-3 text-sm font-light">
              <li><Link to="/listings" className="link-gold text-gold" data-testid="footer-link-listings">Listings</Link></li>
              <li><Link to="/insights" className="link-gold text-bone/90" data-testid="footer-link-insights">Insights</Link></li>
              <li><Link to="/about" className="link-gold text-bone/90" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/crm" className="link-gold text-bone/50" data-testid="footer-link-crm">Broker Login</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <div className="overline overline-gold mb-5">The Quarterly</div>
            <p className="text-bone/65 text-sm font-light leading-relaxed mb-5">
              Off-market opportunities, yield memos, and Sofia's quarterly take. No more than four a year.
            </p>
            <form
              className="flex items-end gap-3"
              onSubmit={handleNewsletter}
              data-testid="newsletter-form"
            >
              <label htmlFor="newsletter-email" className="sr-only">Email address for The Quarterly</label>
              <input
                id="newsletter-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent border-0 border-b border-bone/30 focus:border-gold py-3 text-sm text-bone placeholder:text-bone/40 outline-none font-light"
                data-testid="newsletter-email-input"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="border-b border-bone/30 hover:border-gold text-[0.7rem] tracking-caps uppercase font-medium py-3 text-bone hover:text-gold transition-colors disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {submitting ? "…" : "Subscribe"}
              </button>
            </form>
            <div className="mt-6 flex items-center gap-5 text-bone/60">
              <a href="mailto:hello@sofiasandsrealty.ae" aria-label="Email" className="hover:text-gold transition-colors" data-testid="footer-email">
                <Mail size={18} />
              </a>
              <a href="https://www.instagram.com/sofia_sands_realty?igsh=cHpiaDlyYWl3czZx&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-gold transition-colors" data-testid="footer-instagram">
                <Instagram size={18} />
              </a>
              <a href="https://www.linkedin.com/company/sofia-sands-realty/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-gold transition-colors" data-testid="footer-linkedin">
                <Linkedin size={18} />
              </a>
            </div>
            {/* Office address: stated once, on the homepage only (Yitayal 2026-10-31); must match Google Business Profile */}
            {pathname === "/" && (
              <a
                href="https://maps.google.com/?cid=6634131096182007739"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-start gap-2 text-[0.95rem] leading-relaxed text-bone/70 hover:text-gold transition-colors"
                data-testid="footer-address"
              >
                <MapPin size={18} className="mt-1 shrink-0" />
                <span>Office 203, Khalid Shaban Tower, Al Garhoud, Dubai</span>
              </a>
            )}
          </div>
        </div>

        <div className="h-px bg-bone/15 mt-16 mb-8" />

        {/* Quiet internal link row — real anchors, present in prerendered HTML. */}
        <ExploreLinks tone="dark" className="mb-8" heading="More to explore" />

        <div className="h-px bg-bone/15 mb-6" />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[0.72rem] tracking-[0.12em] uppercase text-bone/45 font-medium">
          <div>© {new Date().getFullYear()} Sofia Sands Realty LLC</div>
          <div className="flex flex-wrap gap-6">
            <span>RERA Permit 41793</span>
            <span>DLD (Dubai) &middot; RAK Municipality (RAK)</span>
            <span className="opacity-60">EN &middot; AR &middot; RU (soon)</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
